import { getMinPrice, getTotalPrice } from '@shared/lib/money';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MutationTuple } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-boost';
import { GraphQLError } from 'graphql';

import {
	SelectedProgramsByTravellers,
	CheckinTraveller,
	InsuranceFragment,
	InsuranceProgramFragment,
	OrderSelectedServices,
	SelectedInsuranceFragment
} from '@websky/graphql';
import { SpecialDocumentPassenger } from '../../../Insurance/InsuranceInner/SpecialDocument/SpecialDocumentField/SpecialDocumentField';
import { PassengersState } from '../../../Checkout/store/passengers/reducers';
import { Money as MoneyType } from '../../../types';
import { ServiceButton } from '../../types';
import { useConfig } from '../../../context';
import { useToggleable } from '../../../hooks';
import { getTravellerName } from '../../../utils';
import { useQueryEventToggleable } from '../../../Checkout/components/Checkout/hooks';
import { SpecialServiceType } from '../../../AIPurchasePredictionService/types';

const isCheckoutPassengerGuard = (passenger: PassengersState | CheckinTraveller): passenger is PassengersState => {
	return !(passenger as CheckinTraveller).services;
};

interface UseInsuranceProps<T> {
	orderId: string;
	passengers: (PassengersState | CheckinTraveller)[];
	insurances: InsuranceFragment;
	insurancePrograms: InsuranceProgramFragment[];
	loading?: boolean;
	onSaveServices: MutationTuple<T, { params: OrderSelectedServices }>[0];
	onUpdateOrder?: (order: T) => void;
}

export const useInsurance = <T>({
	orderId,
	insurances,
	insurancePrograms,
	passengers,
	loading,
	onSaveServices,
	onUpdateOrder
}: UseInsuranceProps<T>) => {
	const { t } = useTranslation('Checkout');

	const {
		global: {
			companyInfo: {
				multiInsurance,
				insurance: insuranceRulesUrl,
				specialDocumentForInsurance: insuranceDocuments
			}
		}
	} = useConfig();

	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setError] = React.useState<GraphQLError>(null);

	const isLoadingInsurance = isLoading || loading;

	const { isOpen: insuranceModalIsOpen, open: openInsuranceModal, close: closeInsuranceModal } = useToggleable(false);
	const { isOpen: downloadModalIsOpen, open: openDownloadModal, close: closeDownloadModal } = useToggleable(false);

	const {
		isOpen: confirmationModalIsOpen,
		open: openConfirmationModal,
		close: closeConfirmationModal
	} = useToggleable(false);

	const initialState = passengers.map(passenger => {
		const insurancePassenger = insurances?.travellerExtraValues?.find(
			traveller => traveller.traveller.id === passenger.id
		);

		const fullName = isCheckoutPassengerGuard(passenger) ? getTravellerName(passenger) : '';

		return {
			name: fullName,
			value: insurancePassenger?.extraValues?.[0]?.value || '',
			id: passenger.id,
			regex: insurancePassenger?.extraValues?.[0]?.validationRules[0].regExp[0],
			isRequired: insurancePassenger?.needKzIinDocument,
			disabled: !!insurancePassenger?.extraValues?.[0]?.value
		};
	});

	const [passengerDocuments, setPassengerDocuments] = React.useState<SpecialDocumentPassenger[]>(initialState);

	const availablePrograms = new Set(insurancePrograms?.map(program => program.code) ?? []);

	const selectedInsurances = React.useMemo(() => {
		const selectedInsuranceMap = new Map<string, SelectedInsuranceFragment>();

		if (insurances?.selectedInsurances?.length) {
			insurances.selectedInsurances.forEach(insurance => {
				const insuranceCode = insurance.insuranceProgram.code;
				const key = `${insuranceCode}-${insurance.passengerName}`;

				// exclude duplicate selected insurances
				if (availablePrograms.has(insuranceCode) && !selectedInsuranceMap.has(key)) {
					selectedInsuranceMap.set(key, insurance);
				}
			});
		}

		return [...selectedInsuranceMap.values()];
	}, [insurances, availablePrograms]);

	useQueryEventToggleable({
		serviceTypes: [SpecialServiceType.Insurance],
		insuranceCodes: [...availablePrograms],
		onOpen: openConfirmationModal
	});

	const onSave = async (selectedCodes: string[], passengersWithDoc?: SpecialDocumentPassenger[]) => {
		setIsLoading(true);

		const selectedInsuranceCodes = insurances.selectedInsurances
			.filter(insurance => !availablePrograms.has(insurance.insuranceProgram.code))
			.map(insurance => insurance.insuranceProgram.code);

		const selectedProgramCodes = [...new Set([...selectedInsuranceCodes, ...selectedCodes])];

		const programsByPassengers: SelectedProgramsByTravellers[] = passengers.map(passenger => {
			const travellerDocumentNumber = passengersWithDoc
				? passengersWithDoc.find(traveller => traveller.id === passenger.id).value
				: '';

			return {
				travellerId: passenger.id,
				programCodes: selectedProgramCodes,
				travellerDocumentNumber
			};
		});

		setPassengerDocuments(passengersWithDoc);

		try {
			const { data } = await onSaveServices({
				variables: {
					params: {
						id: orderId,
						insurances: [
							{
								selectedPrograms: selectedProgramCodes,
								...(multiInsurance ? { selectedProgramsByTravellers: programsByPassengers } : {})
							}
						]
					}
				}
			});

			if (data) {
				onUpdateOrder(data);
			}
		} catch (e) {
			const error = e as ApolloError;

			if (error.graphQLErrors?.length) {
				setError(error.graphQLErrors[0] as GraphQLError);
			}
		}

		setIsLoading(false);
		closeInsuranceModal();
	};

	const onAddInsurance = async () => {
		if (multiInsurance) {
			openInsuranceModal();
		} else if (selectedInsurances.length) {
			openConfirmationModal();
		} else {
			await onSave([insurancePrograms[0].code]);
		}
	};

	const onRemoveInsurance = async () => {
		await onSave([]);
		closeConfirmationModal();
	};

	const clearError = () => {
		setError(null);
	};

	const buttons = React.useMemo(() => {
		let buttons: ServiceButton[] = [
			{
				action: onAddInsurance,
				text: t(multiInsurance ? 'Change' : 'Cancel'),
				isLoading: isLoadingInsurance,
				buttonType: 'change'
			}
		];

		if (selectedInsurances.some(insurance => insurance.policyUrl) && !isLoadingInsurance) {
			buttons = [
				...buttons,
				{
					action: () => {
						if (selectedInsurances.length > 1) {
							openDownloadModal();
						} else {
							window.open(selectedInsurances[0].policyUrl, '_blank');
						}
					},
					text: t('Download')
				}
			];
		}

		if (insuranceRulesUrl && !isLoadingInsurance) {
			buttons = [
				...buttons,
				{
					action: () => window.open(insuranceRulesUrl),
					text: t('AdditionalServiceCard:Terms of service')
				}
			];
		}

		return buttons;
	}, [selectedInsurances, isLoadingInsurance]);

	const minPrice = React.useMemo(() => {
		if (insurancePrograms?.length) {
			return getMinPrice(insurancePrograms, program => program.totalPrice);
		}

		return null;
	}, [insurancePrograms]);

	const totalPrice = React.useMemo<MoneyType>(() => {
		return getTotalPrice(selectedInsurances, insurance => insurance.passengerPrice);
	}, [selectedInsurances]);

	return {
		// common
		multiInsurance,
		insuranceDocuments,
		insuranceRulesUrl,
		passengerDocuments,
		selectedInsurances,
		buttons,
		minPrice,
		totalPrice,
		error,
		onSave,
		onAddInsurance,
		onRemoveInsurance,
		clearError,

		// insurance modal
		insuranceModalIsOpen,
		openInsuranceModal,
		closeInsuranceModal,

		// confirmation modal
		confirmationModalIsOpen,
		openConfirmationModal,
		closeConfirmationModal,

		// download modal
		downloadModalIsOpen,
		openDownloadModal,
		closeDownloadModal
	};
};
